import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import { getToken } from '../../lib/api/core';
import LogoutButton from '../../components/common/LogoutButton';
import ProgressMonitorModal from '../../components/admin/ProgressMonitorModal';

interface Course {
  id: number;
  title: string;
  description?: string;
}

interface Stage {
  id: number;
  course_id: number;
  title: string;
  sort_order: number;
  lessons?: Lesson[];
}

interface Lesson {
  id: number;
  stage_id: number;
  title: string;
  learning_objectives?: string;
  sort_order: number;
}

interface PlanTask {
  lessonId: number;
  lessonTitle: string;
  artifactId: number | null;
  status: 'pending' | 'creating' | 'running' | 'stopped' | 'done' | 'error';
  currentStage: string;
  currentStep: string;
  progress: number;
  error?: string;
  ws?: WebSocket;
}

const BatchPlanGenerationPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [selectedLessons, setSelectedLessons] = useState<Set<number>>(new Set());
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [tasks, setTasks] = useState<Map<number, PlanTask>>(new Map());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const tasksRef = useRef(tasks);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      fetchStagesAndLessons(selectedCourseId);
    }
  }, [selectedCourseId]);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses/');
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchStagesAndLessons = async (courseId: number) => {
    try {
      const stagesRes = await api.get(`/courses/${courseId}/stages/`);
      const stagesData = stagesRes.data;
      
      for (const stage of stagesData) {
        const lessonsRes = await api.get(`/courses/stages/${stage.id}/lessons/`);
        stage.lessons = lessonsRes.data;
      }
      
      setStages(stagesData);
      setExpandedStages(new Set(stagesData.map((s: Stage) => s.id)));
    } catch (error) {
      console.error('Failed to fetch stages and lessons:', error);
    }
  };

  const toggleStage = (stageId: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageId)) {
      newExpanded.delete(stageId);
    } else {
      newExpanded.add(stageId);
    }
    setExpandedStages(newExpanded);
  };

  const toggleLesson = (lessonId: number) => {
    const newSelected = new Set(selectedLessons);
    if (newSelected.has(lessonId)) {
      newSelected.delete(lessonId);
    } else {
      newSelected.add(lessonId);
    }
    setSelectedLessons(newSelected);
  };

  const toggleAllLessonsInStage = (stageId: number, lessonIds: number[]) => {
    const newSelected = new Set(selectedLessons);
    const allSelected = lessonIds.every(id => newSelected.has(id));
    
    if (allSelected) {
      lessonIds.forEach(id => newSelected.delete(id));
    } else {
      lessonIds.forEach(id => newSelected.add(id));
    }
    
    setSelectedLessons(newSelected);
  };

  const getStepProgress = (stepKey: string): number => {
    // Bootstrap
    if (stepKey === 'bootstrap') return 5;
    
    // 整体方案
    if (stepKey === 'blueprint_generate') return 5;
    if (stepKey === 'blueprint_audit') return 7;
    if (stepKey.startsWith('blueprint_repair')) return 9;
    
    // 环节处理
    const phaseMatch = stepKey.match(/phase_(\d+)_(.+)/);
    if (phaseMatch) {
      const phaseNum = parseInt(phaseMatch[1]);
      const action = phaseMatch[2];
      const baseProgress = 10 + (phaseNum - 1) * 10; // 每个环节占10%
      
      const actionProgress: Record<string, number> = {
        'pos_options': 1,
        'pos_best': 2,
        'neg_options': 3,
        'neg_best': 4,
        'judge': 5,
        'judge_retry': 6,
        'detail_generate': 7,
        'detail_audit': 8,
        'detail_repair': 9,
      };
      
      for (const [key, value] of Object.entries(actionProgress)) {
        if (action.startsWith(key)) {
          return baseProgress + value;
        }
      }
      return baseProgress;
    }
    
    // 完整教案
    if (stepKey === 'full_plan_part_1') return 85;
    if (stepKey === 'full_plan_part_2') return 88;
    if (stepKey === 'full_plan_part_3') return 90;
    if (stepKey === 'full_plan_part_4') return 92;
    if (stepKey === 'full_plan_part_5') return 95;
    
    // 最终审核
    if (stepKey === 'final_audit') return 98;
    
    return 0;
  };

  const getStageName = (stepKey: string): string => {
    if (stepKey === 'bootstrap') return '启动中';
    if (stepKey.startsWith('blueprint')) return '整体方案设计';
    if (stepKey.startsWith('phase')) {
      const match = stepKey.match(/phase_(\d+)_/);
      if (match) {
        return `环节${match[1]}`;
      }
      return '环节生成';
    }
    if (stepKey.startsWith('full_plan')) return '完整教案生成';
    if (stepKey === 'final_audit') return '最终审核';
    return '处理中';
  };

  const getStepName = (stepKey: string): string => {
    const stepNames: Record<string, string> = {
      'bootstrap': '初始化',
      'blueprint_generate': '生成方案',
      'blueprint_audit': '审核方案',
      'blueprint_repair': '优化方案',
      'pos_options': '正方备选',
      'pos_best': '正方最优',
      'neg_options': '反方备选',
      'neg_best': '反方最优',
      'judge': '第三方评审',
      'judge_retry': '评审重试',
      'detail_generate': '生成详细方案',
      'detail_audit': '审核详细方案',
      'detail_repair': '优化详细方案',
      'full_plan_part_1': '第1部分',
      'full_plan_part_2': '第2部分',
      'full_plan_part_3': '第3部分',
      'full_plan_part_4': '第4部分',
      'full_plan_part_5': '第5部分',
      'final_audit': '最终审核',
    };
    
    for (const [key, name] of Object.entries(stepNames)) {
      if (stepKey.includes(key)) return name;
    }
    return stepKey;
  };

  const createPlanForLesson = async (lessonId: number): Promise<number | null> => {
    try {
      const res = await api.post(`/courses/lessons/${lessonId}/plans/draft/`, {
        base_artifact_id: null
      });
      return res.data.id;
    } catch (error) {
      console.error(`Failed to create plan for lesson ${lessonId}:`, error);
      return null;
    }
  };

  const startWebSocketGeneration = (lessonId: number, artifactId: number, lessonTitle: string) => {
    const token = getToken();
    const wsUrl = `${import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000/api/v1'}/workshop/ws/multi-agent/generate/${lessonId}/${artifactId}?token=${token}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'start',
        prompt_id: 9,
        model: 'gemini',
        stream_content: false
      }));
      
      setTasks(prev => {
        const newTasks = new Map(prev);
        const task = newTasks.get(lessonId);
        if (task) {
          task.ws = ws;
          task.status = 'running';
          newTasks.set(lessonId, task);
        }
        return newTasks;
      });
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'meta') {
          const progress = getStepProgress(data.step_key);
          const stageName = getStageName(data.step_key);
          const stepName = getStepName(data.step_key);
          
          setTasks(prev => {
            const newTasks = new Map(prev);
            const task = newTasks.get(lessonId);
            if (task) {
              task.currentStage = stageName;
              task.currentStep = stepName;
              task.progress = progress;
              newTasks.set(lessonId, task);
            }
            return newTasks;
          });
        } else if (data.type === 'done') {
          setTasks(prev => {
            const newTasks = new Map(prev);
            const task = newTasks.get(lessonId);
            if (task) {
              task.status = 'done';
              task.progress = 100;
              task.ws?.close();
              newTasks.set(lessonId, task);
            }
            return newTasks;
          });
        } else if (data.type === 'stop_ack') {
          setTasks(prev => {
            const newTasks = new Map(prev);
            const task = newTasks.get(lessonId);
            if (task) {
              task.status = 'stopped';
              task.ws?.close();
              newTasks.set(lessonId, task);
            }
            return newTasks;
          });
        } else if (data.type === 'error') {
          setTasks(prev => {
            const newTasks = new Map(prev);
            const task = newTasks.get(lessonId);
            if (task) {
              task.status = 'error';
              task.error = data.content || 'Unknown error';
              task.ws?.close();
              newTasks.set(lessonId, task);
            }
            return newTasks;
          });
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error(`WebSocket error for lesson ${lessonId}:`, error);
      setTasks(prev => {
        const newTasks = new Map(prev);
        const task = newTasks.get(lessonId);
        if (task) {
          task.status = 'error';
          task.error = 'WebSocket connection error';
          newTasks.set(lessonId, task);
        }
        return newTasks;
      });
    };

    ws.onclose = () => {
      console.log(`WebSocket closed for lesson ${lessonId}`);
    };
  };

  const stopTask = (lessonId: number) => {
    const task = tasksRef.current.get(lessonId);
    if (task && task.ws && task.status === 'running') {
      task.ws.send(JSON.stringify({ type: 'stop' }));
    }
  };

  const resumeTask = async (lessonId: number) => {
    const task = tasksRef.current.get(lessonId);
    if (!task || !task.artifactId) return;

    setTasks(prev => {
      const newTasks = new Map(prev);
      const currentTask = newTasks.get(lessonId);
      if (currentTask) {
        currentTask.status = 'running';
        newTasks.set(lessonId, currentTask);
      }
      return newTasks;
    });

    const token = getToken();
    const wsUrl = `${import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000/api/v1'}/workshop/ws/multi-agent/generate/${lessonId}/${task.artifactId}?token=${token}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'start',
        prompt_id: 9,
        model: 'gemini',
        stream_content: false,
        resume: true
      }));
      
      setTasks(prev => {
        const newTasks = new Map(prev);
        const currentTask = newTasks.get(lessonId);
        if (currentTask) {
          currentTask.ws = ws;
          newTasks.set(lessonId, currentTask);
        }
        return newTasks;
      });
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'meta') {
          const progress = getStepProgress(data.step_key);
          const stageName = getStageName(data.step_key);
          const stepName = getStepName(data.step_key);
          
          setTasks(prev => {
            const newTasks = new Map(prev);
            const currentTask = newTasks.get(lessonId);
            if (currentTask) {
              currentTask.currentStage = stageName;
              currentTask.currentStep = stepName;
              currentTask.progress = progress;
              newTasks.set(lessonId, currentTask);
            }
            return newTasks;
          });
        } else if (data.type === 'done') {
          setTasks(prev => {
            const newTasks = new Map(prev);
            const currentTask = newTasks.get(lessonId);
            if (currentTask) {
              currentTask.status = 'done';
              currentTask.progress = 100;
              currentTask.ws?.close();
              newTasks.set(lessonId, currentTask);
            }
            return newTasks;
          });
        } else if (data.type === 'stop_ack') {
          setTasks(prev => {
            const newTasks = new Map(prev);
            const currentTask = newTasks.get(lessonId);
            if (currentTask) {
              currentTask.status = 'stopped';
              currentTask.ws?.close();
              newTasks.set(lessonId, currentTask);
            }
            return newTasks;
          });
        } else if (data.type === 'error') {
          setTasks(prev => {
            const newTasks = new Map(prev);
            const currentTask = newTasks.get(lessonId);
            if (currentTask) {
              currentTask.status = 'error';
              currentTask.error = data.content || 'Unknown error';
              currentTask.ws?.close();
              newTasks.set(lessonId, currentTask);
            }
            return newTasks;
          });
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error(`WebSocket error for lesson ${lessonId}:`, error);
      setTasks(prev => {
        const newTasks = new Map(prev);
        const currentTask = newTasks.get(lessonId);
        if (currentTask) {
          currentTask.status = 'error';
          currentTask.error = 'WebSocket connection error';
          newTasks.set(lessonId, currentTask);
        }
        return newTasks;
      });
    };

    ws.onclose = () => {
      console.log(`WebSocket closed for lesson ${lessonId}`);
    };
  };

  const startBatchGeneration = async () => {
    if (selectedLessons.size === 0) {
      alert('请至少选择一个课时');
      return;
    }

    setIsGenerating(true);
    setIsModalOpen(true);
    setIsModalMinimized(false);
    const newTasks = new Map<number, PlanTask>();

    for (const lessonId of selectedLessons) {
      let lessonTitle = '';
      for (const stage of stages) {
        const lesson = stage.lessons?.find(l => l.id === lessonId);
        if (lesson) {
          lessonTitle = lesson.title;
          break;
        }
      }
      
      newTasks.set(lessonId, {
        lessonId,
        lessonTitle,
        artifactId: null,
        status: 'creating',
        currentStage: '',
        currentStep: '',
        progress: 0
      });
    }

    setTasks(newTasks);

    for (const lessonId of selectedLessons) {
      setTasks(prev => {
        const newTasks = new Map(prev);
        const task = newTasks.get(lessonId);
        if (task) {
          task.status = 'creating';
          newTasks.set(lessonId, task);
        }
        return newTasks;
      });

      const artifactId = await createPlanForLesson(lessonId);
      
      if (artifactId) {
        setTasks(prev => {
          const newTasks = new Map(prev);
          const task = newTasks.get(lessonId);
          if (task) {
            task.artifactId = artifactId;
            newTasks.set(lessonId, task);
          }
          return newTasks;
        });

        startWebSocketGeneration(lessonId, artifactId, newTasks.get(lessonId)?.lessonTitle || '');
      } else {
        setTasks(prev => {
          const newTasks = new Map(prev);
          const task = newTasks.get(lessonId);
          if (task) {
            task.status = 'error';
            task.error = 'Failed to create plan';
            newTasks.set(lessonId, task);
          }
          return newTasks;
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-all"
          >
            <ArrowLeft size={16} />
            返回管理控制台
          </button>
          <h1 className="text-xl font-bold text-gray-900">批量生成教案</h1>
        </div>
        <LogoutButton />
      </header>

      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">选择课程</h2>
            <select
              value={selectedCourseId || ''}
              onChange={(e) => setSelectedCourseId(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">请选择课程</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {selectedCourseId && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">选择课时</h2>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">
                    已选择 {selectedLessons.size} 个课时
                  </span>
                  <button
                    onClick={startBatchGeneration}
                    disabled={isGenerating || selectedLessons.size === 0}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Play size={16} />
                    开始批量生成
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {stages.map(stage => (
                  <div key={stage.id} className="border rounded-lg overflow-hidden">
                    <div
                      className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleStage(stage.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedStages.has(stage.id) ? (
                          <ChevronDown size={20} className="text-gray-500" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-500" />
                        )}
                        <span className="font-medium text-gray-900">{stage.title}</span>
                        <span className="text-sm text-gray-500">
                          ({stage.lessons?.length || 0} 课时)
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAllLessonsInStage(stage.id, stage.lessons?.map(l => l.id) || []);
                        }}
                        className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                      >
                        {stage.lessons?.every(l => selectedLessons.has(l.id)) ? '取消全选' : '全选'}
                      </button>
                    </div>

                    {expandedStages.has(stage.id) && (
                      <div className="p-4 grid grid-cols-2 gap-3">
                        {stage.lessons?.map(lesson => (
                          <label
                            key={lesson.id}
                            className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedLessons.has(lesson.id)}
                              onChange={() => toggleLesson(lesson.id)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="inline-flex items-center justify-center w-8 h-6 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                              L{lesson.sort_order}
                            </span>
                            <span className="text-sm text-gray-700 flex-1">{lesson.title}</span>
                            <span className="text-xs text-gray-400">ID: {lesson.id}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Monitor Modal */}
      <ProgressMonitorModal
        isOpen={isModalOpen && !isModalMinimized}
        onClose={() => setIsModalOpen(false)}
        onMinimize={() => setIsModalMinimized(true)}
        tasks={tasks}
        onStop={stopTask}
        onResume={resumeTask}
      />

      {/* Minimized Status Bar */}
      {isModalMinimized && tasks.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-[9998]">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-gray-900">
                  批量生成进行中
                </span>
              </div>
              <div className="text-sm text-gray-500">
                {Array.from(tasks.values()).filter(t => t.status === 'done').length}/{tasks.size} 完成
              </div>
            </div>
            <button
              onClick={() => setIsModalMinimized(false)}
              className="px-4 py-1.5 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors"
            >
              查看详情
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchPlanGenerationPage;
